<template>
  <div class="jdv2" v-if="job">
    <div class="jdv2__hero">
      <div class="jdv2__hero-copy">
        <span v-if="contractLabel" class="jdv2__badge">
          <v-icon x-small color="#2563eb">mdi-file-document-outline</v-icon>
          {{ contractLabel }}
        </span>
        <h1>{{ title }}</h1>
        <div v-if="descriptionHtml" class="jdv2__prose" v-html="descriptionHtml"></div>
        <div class="jdv2__pills">
          <span v-if="placeLabel">
            <v-icon x-small>mdi-map-marker-outline</v-icon>{{ placeLabel }}
          </span>
          <span>
            <v-icon x-small>mdi-calendar-blank-outline</v-icon>{{ dates }}
          </span>
          <span class="jdv2__pills-rating">
            Rating
            <v-rating
              color="#4db6ac"
              background-color="#4db6ac"
              empty-icon="mdi-star-outline"
              full-icon="mdi-star"
              half-icon="mdi-star-half-full"
              half-increments
              dense
              length="5"
              readonly
              size="18"
              :value="Number(job.rating) || 0"
            />
          </span>
        </div>
      </div>
      <div class="jdv2__hero-media">
        <img :src="imageSrc" :alt="title" @error="onImageError" />
      </div>
    </div>

    <v-divider class="jdv2__divider " />

    <div class="jdv2__grid">
      <div class="jdv2__main">
        <section class="jdv2__card">
          <div v-if="sectors.length" class="jdv2__block">
            <h2>
              <v-icon color="#2563eb">mdi-briefcase-outline</v-icon>
              Professional Sector
            </h2>
            <ul class="jdv2__list">
              <li v-for="(item, index) in sectors" :key="'sector-' + index">{{ item }}</li>
            </ul>
          </div>
          <p v-else class="jdv2__block"><strong>Professional Sector:</strong> Not specified</p>

          <div v-if="requirements.length" class="jdv2__block">
            <h2>
              <v-icon color="#2563eb">mdi-clipboard-text-outline</v-icon>
              Job Requeriments
            </h2>
            <ul class="jdv2__list">
              <li v-for="(item, index) in requirements" :key="'req-' + index">{{ item }}</li>
            </ul>
          </div>
          <p v-else class="jdv2__block"><strong>Job Requeriments:</strong> Not specified</p>

          <div v-if="demandedSkills.length" class="jdv2__block">
            <h2>
              <v-icon color="#2563eb">mdi-star-outline</v-icon>
              Demanded Skills
            </h2>
            <div class="jdv2__chips">
              <span v-for="(item, index) in demandedSkills" :key="'ess-' + index">{{ item }}</span>
            </div>
            <p v-if="visitor" class="jdv2__hint">
              * This service uses the ESCO classification of the European Commission.
            </p>
          </div>
          <p v-else class="jdv2__block"><strong>Demanded Skills:</strong> Not specified</p>

          <div v-if="optionalSkills.length" class="jdv2__block">
            <h2>
              <v-icon color="#2563eb">mdi-star-plus-outline</v-icon>
              Optional Skills
            </h2>
            <div class="jdv2__chips jdv2__chips--soft">
              <span v-for="(item, index) in optionalSkills" :key="'opt-' + index">{{ item }}</span>
            </div>
            <p v-if="visitor" class="jdv2__hint">
              * This service uses the ESCO classification of the European Commission.
            </p>
          </div>
        </section>

        <section v-if="resources.length" class="jdv2__card">
          <h2>
            <v-icon color="#2563eb">mdi-paperclip</v-icon>
            Resources
          </h2>
          <ul class="jdv2__resources">
            <li v-for="res in resources" :key="res.resId">
              <a :href="resourceHref(res)" target="_blank" rel="noopener">
                <v-icon small color="#2563eb">
                  {{ res.resType === "File" ? "mdi-file-outline" : "mdi-link-variant" }}
                </v-icon>
                <span>
                  <strong>{{ res.resName }}</strong>
                  <em v-if="res.resDesc">{{ res.resDesc }}</em>
                  <small>{{ res.resType === "File" ? "File" : "Link" }}</small>
                </span>
              </a>
            </li>
          </ul>
        </section>

        <section class="jdv2__card">
          <h2>
            <v-icon color="#2563eb">mdi-information-outline</v-icon>
            More information
          </h2>
          <div class="jdv2__info-cols">
            <table
              v-for="(column, index) in infoColumns"
              :key="index"
              class="jdv2__table"
            >
              <tr v-for="row in column" :key="row.label">
                <th>{{ row.label }}</th>
                <td>
                  <template v-for="(part, partIndex) in linkParts(row.value)">
                    <a
                      v-if="part.href"
                      :key="row.label + '-a-' + partIndex"
                      :href="part.href"
                      target="_blank"
                      rel="noopener"
                    >{{ part.text }}</a>
                    <span v-else :key="row.label + '-t-' + partIndex">{{ part.text }}</span>
                  </template>
                </td>
              </tr>
            </table>
          </div>
        </section>
      </div>

      <aside class="jdv2__side">
        <div v-if="!visitor" class="jdv2__card jdv2__side-card">
          <button
            v-if="!isFav"
            type="button"
            class="jdv2__btn jdv2__btn--primary"
            @click="$emit('add-fav')"
          >
            Add Job To Favorites
          </button>
          <button
            v-else
            type="button"
            class="jdv2__btn jdv2__btn--ghost"
            @click="$emit('del-fav')"
          >
            Remove From Favorites
          </button>
        </div>

        <div class="jdv2__card">
          <h3>Quick information</h3>
          <ul class="jdv2__facts">
            <li v-if="contractLabel">
              <v-icon small>mdi-file-document-outline</v-icon>
              <span>Contract Type</span>
              <b>{{ contractLabel }}</b>
            </li>
            <li>
              <v-icon small>mdi-calendar-blank-outline</v-icon>
              <span>Dates</span>
              <b>{{ dates }}</b>
            </li>
            <li v-if="placeLabel">
              <v-icon small>mdi-map-marker-outline</v-icon>
              <span>Job place</span>
              <b>{{ placeLabel }}</b>
            </li>
            <li v-if="salaryLabel">
              <v-icon small>mdi-cash</v-icon>
              <span>Gross salary</span>
              <b>{{ salaryLabel }}</b>
            </li>
          </ul>
        </div>

        <div class="jdv2__card">
          <h3>Company</h3>
          <p class="jdv2__org">{{ companyName }}</p>
          <p v-if="job.contact_details" class="jdv2__meta-line">
            <v-icon x-small>mdi-email-outline</v-icon>
            <span>
              <template v-for="(part, partIndex) in linkParts(job.contact_details)">
                <a
                  v-if="part.href"
                  :key="'c-a-' + partIndex"
                  :href="part.href"
                  target="_blank"
                  rel="noopener"
                >{{ part.text }}</a>
                <span v-else :key="'c-t-' + partIndex">{{ part.text }}</span>
              </template>
            </span>
          </p>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
const dash = "Not specified"
const missing = "Not defined"
const missingLang = "Not definded"

const asList = (value) => {
  if (!value) return []
  if (Array.isArray(value)) return value.filter(Boolean)
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) return parsed.filter(Boolean)
    } catch (error) {
      return []
    }
  }
  return []
}

const joinList = (value, empty) => {
  const items = asList(value)
  return items.length ? items.join(", ") : empty
}

const URL_RE = /https?:\/\/[^\s]+/gi

const linkParts = (text) => {
  const raw = String(text || "")
  const parts = []
  let last = 0
  const re = new RegExp(URL_RE.source, URL_RE.flags)
  let match
  while ((match = re.exec(raw))) {
    if (match.index > last) {
      parts.push({ text: raw.slice(last, match.index) })
    }
    const token = match[0]
    const href = token.replace(/[),.;:'"]+$/g, "")
    parts.push({ text: href, href })
    if (href.length < token.length) {
      parts.push({ text: token.slice(href.length) })
    }
    last = match.index + token.length
  }
  if (last < raw.length) parts.push({ text: raw.slice(last) })
  return parts.length ? parts : [{ text: raw }]
}

export default {
  name: "JobDetailPage",
  props: {
    job: { type: Object, required: true },
    visitor: { type: Boolean, default: false },
  },
  data: () => ({
    imageFailed: false,
    resources: [],
  }),
  computed: {
    title() {
      const name = String((this.job && this.job.name) || "").toLowerCase()
      if (!name) return ""
      return name.charAt(0).toUpperCase() + name.slice(1)
    },
    descriptionHtml() {
      return String(this.job.description || "")
        .replace(/<\s*\/\s*br\s*>/gi, "<br>")
        .replace(/<\s*br\s*>/gi, "<br>")
    },
    sectors() {
      return asList(this.job.sector).map((text) => {
        const value = String(text)
        return value.length > 2 ? value.charAt(0).toUpperCase() + value.slice(1) : value
      })
    },
    demandedSkills() {
      return asList(this.job.essentialSkillsSelected)
    },
    optionalSkills() {
      return asList(this.job.optionalSkillsSelected)
    },
    requirements() {
      return asList(this.job.job_requirements)
    },
    contractLabel() {
      return joinList(this.job.contract_types, "")
    },
    languagesLabel() {
      return joinList(this.job.languages, missingLang)
    },
    placeLabel() {
      return this.job.place || ""
    },
    salaryLabel() {
      return this.job.salary ? this.job.salary + " €" : ""
    },
    companyName() {
      return this.job.full_name || missing
    },
    isFav() {
      return this.job.fav_id != null
    },
    dates() {
      if (this.job.init_date && this.job.end_date) {
        return (
          new Date(this.job.init_date).toLocaleDateString() +
          " - " +
          new Date(this.job.end_date).toLocaleDateString()
        )
      }
      if (!this.visitor && this.job.init_date) {
        return new Date(this.job.init_date).toLocaleDateString() + " (Start date)"
      }
      return dash
    },
    createdAt() {
      return this.job.created_at
        ? new Date(this.job.created_at).toLocaleDateString()
        : missing
    },
    imageSrc() {
      const file = this.imageFailed || !this.job.image ? "default.jpg" : this.job.image
      return process.env.VUE_APP_API_ENDPOINT + "images/jobs/" + file
    },
    infoRows() {
      return [
        { label: "Company Name", value: this.companyName },
        { label: this.visitor ? "Language used in workspace" : "Language", value: this.languagesLabel },
        { label: "Contract Type", value: this.contractLabel || missingLang },
        { label: "Job place", value: this.placeLabel || missing },
        { label: "Dates", value: this.dates },
        { label: "Gross salary", value: this.salaryLabel || missing },
        { label: "Contact information", value: this.job.contact_details || missing },
        { label: "Date of creation", value: this.createdAt },
      ]
    },
    infoColumns() {
      const rows = this.infoRows
      const mid = Math.ceil(rows.length / 2)
      return [rows.slice(0, mid), rows.slice(mid)]
    },
  },
  watch: {
    "job.job_id": {
      immediate: true,
      handler() {
        this.loadResources()
      },
    },
  },
  methods: {
    linkParts,
    onImageError() {
      this.imageFailed = true
    },
    resourceHref(res) {
      if (res.resType === "File") {
        return process.env.VUE_APP_API_ENDPOINT + "images/resources/" + res.resUrl
      }
      return res.resUrl
    },
    async loadResources() {
      this.resources = []
      const entityId = this.job && this.job.job_id
      if (!entityId) return
      try {
        const formData = new FormData()
        formData.append("entityTable", "jobs")
        formData.append("entityId", entityId)
        let response = await fetch(
          process.env.VUE_APP_API_ENDPOINT + "common/get-resource",
          {
            method: "POST",
            body: formData,
            headers: {
              "Auth-Token": this.$cookie.get("jwt_token"),
            },
          }
        )
        response = await response.json()
        if (!(response.success || response.ok) || !response.result) return
        this.resources = response.result.map((item) => ({
          resId: item.id,
          resType: item.type,
          resName: item.name,
          resDesc: item.desc,
          resUrl: item.content,
        }))
      } catch (error) {
        this.resources = []
      }
    },
  },
}
</script>

<style scoped>
.jdv2 {
  max-width: 1180px;
  margin: 0 auto;
  padding: 16px 8px 48px;
  color: #0f172a;
}

.jdv2__hero {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 28px;
  align-items: center;
  margin-bottom: 20px;
  margin-top:-22px
}

.jdv2__badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 13px;
  font-weight: 600;
  border-radius: 999px;
  padding: 4px 10px;
}

.jdv2__hero-copy h1 {
  font-size: 34px;
  line-height: 1.15;
  margin: 12px 0 12px;
  font-weight: 800;
}

.jdv2__hero-copy .jdv2__prose {
  margin: 0 0 16px;
}

.jdv2__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: #475569;
  font-size: 14px;
}

.jdv2__pills span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.jdv2__pills-rating {
  font-weight: 700;
}

.jdv2__pills-rating >>> .v-rating {
  margin: 0;
}

.jdv2__pills-rating >>> .v-icon {
  padding: 0;
}

.jdv2__hero-media img {
  width: 100%;
  max-height: 220px;
  object-fit: contain;
  background: #fff;
  border-radius: 16px;
}

.jdv2__divider {
  margin: -20px 0 0 !important;
}

.jdv2__grid {
  display: grid;
  grid-template-columns: 1.7fr 0.9fr;
  gap: 20px;
  align-items: start;
}

.jdv2__card {
  background: #fff;
  border-radius: 16px;
  padding: 22px 24px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  margin-bottom: 16px;
}

.jdv2__card h2,
.jdv2__card h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  margin: 0 0 12px;
}

.jdv2__block {
  margin-top: 22px;
}

.jdv2__card > .jdv2__block:first-child,
.jdv2__card > .jdv2__prose:first-child,
.jdv2__card > p.jdv2__block:first-child {
  margin-top: 0;
}

.jdv2__list {
  margin: 0;
  padding-left: 1.2em;
  color: #475569;
  line-height: 1.55;
}

.jdv2__list li + li {
  margin-top: 0.35em;
}

.jdv2__resources {
  list-style: none;
  padding: 0;
  margin: 0;
}

.jdv2__resources li + li {
  margin-top: 8px;
}

.jdv2__resources a {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #0f172a;
  text-decoration: none;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.jdv2__resources a:hover {
  border-color: #bfdbfe;
  background: #f8fafc;
}

.jdv2__resources span {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.jdv2__resources em {
  font-style: normal;
  color: #64748b;
  font-size: 13px;
}

.jdv2__resources small {
  color: #2563eb;
  font-size: 12px;
  font-weight: 600;
}

.jdv2__prose {
  color: #475569;
  line-height: 1.55;
  white-space: pre-line;
}

.jdv2__prose >>> p {
  margin: 0 0 0.6em;
}

.jdv2__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.jdv2__chips span {
  background: #dbeafe;
  color: #1d4ed8;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 13px;
}

.jdv2__chips--soft span {
  background: #f1f5f9;
  color: #334155;
}

.jdv2__info-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 28px;
}

.jdv2__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.jdv2__table th,
.jdv2__table td {
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 8px;
  text-align: left;
  vertical-align: top;
}

.jdv2__table th {
  width: 38%;
  color: #64748b;
  font-weight: 600;
}

.jdv2__side-card .jdv2__btn:first-child {
  margin-top: 0;
}

.jdv2__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  border-radius: 10px;
  padding: 12px 14px;
  font-weight: 600;
  text-decoration: none;
  margin-top: 10px;
  cursor: pointer;
}

.jdv2__btn--primary {
  background: #2563eb;
  color: #fff;
  border: 0;
}

.jdv2__btn--ghost {
  background: #fff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}

.jdv2__facts {
  list-style: none;
  padding: 0;
  margin: 0;
}

.jdv2__facts li {
  display: grid;
  grid-template-columns: 24px 1fr auto;
  gap: 8px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
}

.jdv2__facts span {
  color: #64748b;
}

.jdv2__org {
  font-weight: 700;
  margin: 0 0 10px;
}

.jdv2__hint {
  font-size: 13px;
  color: #475569;
  margin: 6px 0;
}

.jdv2__meta-line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #475569;
  margin: 6px 0;
  word-break: break-all;
}

.jdv2__meta-line .v-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

.jdv2__meta-line a,
.jdv2__table a {
  color: #2563eb;
  word-break: break-all;
}

@media (max-width: 960px) {
  .jdv2__hero,
  .jdv2__grid,
  .jdv2__info-cols {
    grid-template-columns: 1fr;
  }
}
</style>
